import React from 'react';
import { Box, Typography, Avatar, useTheme, useMediaQuery } from '@mui/material';
import { colors } from '../theme/themeConfig';
import { useUser } from '../contexts/UserContext';

interface PageHeaderProps {
  title?: string;
  subtitle?: string;
  greeting?: string;
  showBalance?: boolean;
  actionButton?: React.ReactNode;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  showBalance = true,
  actionButton,
}) => {
  // Get user data from context
  const { user, telegramUser } = useUser();
  
  // Get user info
  const username = telegramUser?.username || user?.telegramUsername || 'User';
  const firstName = telegramUser?.firstName || 'User';
  const displayName = username || firstName;
  
  // Get photo URL from Telegram user if available
  const photoUrl = telegramUser?.photoUrl || null;
  
  // Get bonus balance
  const bonusBalance = user?.bonusBalance || 0;
  const theme = useTheme();
  // Using a single breakpoint at 375px as per user preference
  const isMobile = useMediaQuery('(max-width:375px)');

  return (
    <Box
      sx={{
        position: 'relative',
        marginBottom: isMobile ? '8px' : '16px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch', // Override parent's alignItems
        alignSelf: 'stretch',  // Take full width regardless of parent's alignment
        width: '100%',         // Ensure full width
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* Avatar */}
          <Avatar
            src={photoUrl || undefined}
            alt={displayName}
            sx={{
              width: isMobile ? '32px' : '36px',
              height: isMobile ? '32px' : '36px',
              marginRight: '8px',
              backgroundColor: photoUrl ? 'transparent' : '#333333', // Simple gray circle as fallback
            }}
          />
          
          {/* Username */}
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              fontSize: isMobile ? '13px' : '14px',
              color: colors.text.primary,
            }}
          >
            {displayName}
          </Typography>
        </Box>

        {/* Balance display */}
      {showBalance && (
        <Box sx={{ display: 'flex', gap: '4px' }}>
          <Typography 
            variant="body2" 
            sx={{ 
              fontSize: isMobile ? '12px' : '13px',
              color: colors.text.secondary,
            }}
          >
            Бонусный баланс:
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              fontSize: isMobile ? '12px' : '13px',
              fontWeight: 500,
              color: colors.secondary.main,
            }}
          >
            {bonusBalance} рфб
          </Typography>
        </Box>
      )}
        
        {/* Action button in top right corner */}
        {actionButton}
      </Box>
      
      {/* Title */}
      {title && (
        <Typography
          variant="h1"
          sx={{
            fontSize: isMobile ? '20px' : '24px',
            fontWeight: 'bold',
            marginTop: '8px',
            marginBottom: subtitle ? '2px' : (isMobile ? '8px' : '12px'),
            paddingLeft: isMobile ? '4px' : 0,
            color: colors.text.primary,
          }}
        >
          {title}
        </Typography>
      )}
      
      {/* Subtitle if provided */}
      {subtitle && (
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: isMobile ? '12px' : '14px',
            color: colors.text.secondary,
            marginBottom: isMobile ? '8px' : '10px',
            paddingLeft: isMobile ? '4px' : 0,
          }}
        >
          {subtitle}
        </Typography>
      )}
      
    </Box>
  );
};

export default PageHeader;
